import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define the tools we want to track guest usage for
export type GuestToolType = 'codeEditor' | 'visualCanvas' | 'componentLibrary';

interface GuestUsageContextType {
  // Check if a guest has used a specific tool
  hasUsedTool: (tool: GuestToolType) => boolean;
  
  // Mark a tool as used by the guest
  markToolAsUsed: (tool: GuestToolType) => void;
  
  // Reset usage tracking for all tools (useful when logging out)
  resetAllToolUsage: () => void;
}

const GuestUsageContext = createContext<GuestUsageContextType>({
  hasUsedTool: () => false,
  markToolAsUsed: () => {},
  resetAllToolUsage: () => {},
});

export const useGuestUsage = () => useContext(GuestUsageContext);

interface GuestUsageProviderProps {
  children: ReactNode;
}

export const GuestUsageProvider: React.FC<GuestUsageProviderProps> = ({ children }) => {
  // State to track which tools have been used
  const [usedTools, setUsedTools] = useState<Record<GuestToolType, boolean>>(() => {
    // Initialize from localStorage if available
    try {
      const savedUsage = localStorage.getItem('guestToolUsage');
      return savedUsage ? JSON.parse(savedUsage) : {
        codeEditor: false,
        visualCanvas: false,
        componentLibrary: false
      };
    } catch (error) {
      console.error('Error loading guest usage from localStorage:', error);
      return {
        codeEditor: false,
        visualCanvas: false,
        componentLibrary: false
      };
    }
  });

  // Save to localStorage whenever usedTools changes
  useEffect(() => {
    try {
      localStorage.setItem('guestToolUsage', JSON.stringify(usedTools));
    } catch (error) {
      console.error('Error saving guest usage to localStorage:', error);
    }
  }, [usedTools]);

  // Check if a specific tool has been used
  const hasUsedTool = (tool: GuestToolType): boolean => {
    return usedTools[tool] || false;
  };

  // Mark a tool as used
  const markToolAsUsed = (tool: GuestToolType): void => {
    setUsedTools(prev => ({
      ...prev,
      [tool]: true
    }));
  };

  // Reset all tool usage tracking
  const resetAllToolUsage = (): void => {
    setUsedTools({
      codeEditor: false,
      visualCanvas: false,
      componentLibrary: false
    });
  };

  const value = {
    hasUsedTool,
    markToolAsUsed,
    resetAllToolUsage,
  };

  return (
    <GuestUsageContext.Provider value={value}>
      {children}
    </GuestUsageContext.Provider>
  );
}; 